import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ROLE_LABELS } from "@/lib/format";

const n = (v) => Math.round(Number(v || 0));
const rp = (v) => `Rp ${n(v).toLocaleString("id-ID")}`;

const sections = (d, petugas, resorts) => [
  {
    title: "Ringkasan Keuangan",
    head: ["Keterangan", "Nominal"],
    rows: [
      ["Setoran simpanan", d.totals.setor.total], ["Angsuran diterima", d.totals.angsuran.total],
      ["Penarikan simpanan", d.totals.tarik.total], ["Pencairan pinjaman (bersih)", d.totals.pencairan.total],
      ["Potongan masuk simpanan", d.totals.potongan?.total || 0], ["Biaya administrasi", d.admin_fee_total],
      ["Arus kas bersih", d.net_cash], ["Margin pinjaman (berjalan & lunas)", d.projected_interest],
    ],
    money: [1],
  },
  {
    title: "Kinerja Petugas (transaksi)",
    head: ["Petugas", "Peran", "Jumlah", "Setoran", "Angsuran", "Penarikan", "Pencairan"],
    rows: d.officers.map((o) => [o.name, ROLE_LABELS[o.role], o.count, o.setor, o.angsuran, o.tarik, o.pencairan]),
    money: [3, 4, 5, 6],
  },
  {
    title: "Petugas Lapangan",
    head: ["Petugas", "Resort", "Jumlah Pendanaan", "Anggota Pinjam", "Total Pembiayaan", "Sisa Pinjaman"],
    rows: petugas.map((p) => [p.name, p.resort_name || "-", p.pendanaan, p.anggota_pinjam, p.total_pembiayaan, p.sisa_pinjaman]),
    money: [2, 4, 5],
  },
  {
    title: "Laporan Resort",
    head: ["Resort", "Anggota", "Total Simpanan", "Total Pinjaman", "Sisa Pinjaman"],
    rows: resorts.map((r) => [r.name, r.member_count, r.total_simpanan, r.total_pinjaman, r.sisa_pinjaman]),
    money: [2, 3, 4],
  },
];

const resortMembers = (resorts) => resorts.flatMap((r) => r.members.map((m) => [r.name, m.nik, m.name, m.petugas_name || "-", m.active ? "Aktif" : "Non-aktif", m.savings_balance, m.total_pinjaman, m.outstanding]));

export const logoDataUrl = async (url) => {
  if (!url) return null;
  try {
    const blob = await (await fetch(url)).blob();
    return await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
  } catch { return null; }
};

// generic: sections = [{ title, head: [], rows: [[]], money: [colIdx] }]
export const exportSections = (kind, { title, subtitle, sections, filename, logo }) => {
  if (kind === "excel") {
    const wb = XLSX.utils.book_new();
    sections.forEach((s) => XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([[s.title], [subtitle], [], s.head, ...s.rows]), s.title.slice(0, 30)));
    XLSX.writeFile(wb, `${filename}.xlsx`);
    return;
  }
  const doc = new jsPDF({ orientation: "landscape" });
  let x = 14;
  if (logo) { try { doc.addImage(logo, 14, 8, 16, 16); x = 34; } catch { x = 14; } }
  doc.setFontSize(14); doc.text(title, x, 15);
  doc.setFontSize(10); doc.text(subtitle, x, 21);
  doc.setLineWidth(0.5); doc.line(14, 26, 283, 26);
  let y = 33;
  sections.forEach((s) => {
    if (y > 175) { doc.addPage(); y = 15; }
    doc.setFontSize(11); doc.text(s.title, 14, y);
    autoTable(doc, { startY: y + 2, head: [s.head], body: s.rows.map((r) => r.map((c, i) => ((s.money || []).includes(i) ? rp(c) : c))), styles: { fontSize: 8 }, headStyles: { fillColor: [16, 120, 90] } });
    y = doc.lastAutoTable.finalY + 10;
  });
  doc.save(`${filename}.pdf`);
};

export const exportPdf = ({ d, petugas, resorts, range, appName, office }) => {
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(15); doc.text(appName, 14, 15);
  doc.setFontSize(10); doc.text(`${office} · Periode ${range.start} s/d ${range.end}`, 14, 21);
  let y = 28;
  sections(d, petugas, resorts).forEach((s) => {
    doc.setFontSize(11); doc.text(s.title, 14, y);
    autoTable(doc, { startY: y + 2, head: [s.head], body: s.rows.map((r) => r.map((c, i) => (s.money.includes(i) ? rp(c) : c))), styles: { fontSize: 8 }, headStyles: { fillColor: [16, 120, 90] } });
    y = doc.lastAutoTable.finalY + 10;
    if (y > 180) { doc.addPage(); y = 15; }
  });
  doc.setFontSize(11); doc.text("Daftar Anggota per Resort", 14, y);
  autoTable(doc, { startY: y + 2, head: [["Resort", "NIK", "Nama", "Petugas", "Status", "Simpanan", "Total Pinjaman", "Sisa"]], body: resortMembers(resorts).map((r) => r.map((c, i) => (i >= 5 ? rp(c) : c))), styles: { fontSize: 8 }, headStyles: { fillColor: [16, 120, 90] } });
  doc.save(`laporan-${range.start}_${range.end}.pdf`);
};

export const exportExcel = ({ d, petugas, resorts, range }) => {
  const wb = XLSX.utils.book_new();
  sections(d, petugas, resorts).forEach((s) => {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([[s.title], [`Periode ${range.start} s/d ${range.end}`], [], s.head, ...s.rows]), s.title.slice(0, 30));
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Resort", "NIK", "Nama", "Petugas", "Status", "Simpanan", "Total Pinjaman", "Sisa Pinjaman"], ...resortMembers(resorts)]), "Anggota per Resort");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(d.series), "Transaksi Harian");
  XLSX.writeFile(wb, `laporan-${range.start}_${range.end}.xlsx`);
};
